/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'

export const THEME_STORAGE_KEY = 'wager-studio:theme'

export const THEMES = {
  SYSTEM: 'system',
  LIGHT: 'light',
  DARK: 'dark',
}

const VALID_THEMES = new Set([THEMES.SYSTEM, THEMES.LIGHT, THEMES.DARK])

export function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return THEMES.LIGHT
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.DARK : THEMES.LIGHT
}

export function readStoredTheme() {
  if (typeof window === 'undefined') {
    return THEMES.SYSTEM
  }
  try {
    const stored = window.localStorage?.getItem(THEME_STORAGE_KEY)
    if (stored && VALID_THEMES.has(stored)) {
      return stored
    }
  } catch {
    // Local storage may be restricted in private/sandboxed windows
  }
  return THEMES.SYSTEM
}

export function applyThemeToDocument(themePreference, resolvedTheme) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.setAttribute('data-theme', themePreference)
  root.setAttribute('data-color-scheme', resolvedTheme)
  root.style.colorScheme = resolvedTheme
}

export const ThemeContext = createContext(null)

export function ThemeProvider({ children, initialTheme }) {
  const [theme, setThemeState] = useState(() => initialTheme || readStoredTheme())
  const [systemTheme, setSystemTheme] = useState(getSystemTheme)

  const resolvedTheme = useMemo(() => {
    if (theme === THEMES.SYSTEM) {
      return systemTheme
    }
    return theme === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT
  }, [theme, systemTheme])

  const isDark = resolvedTheme === THEMES.DARK

  // Listen to OS system color scheme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e) => {
      setSystemTheme(e.matches ? THEMES.DARK : THEMES.LIGHT)
    }

    // Set initial system theme accurately
    setSystemTheme(mediaQuery.matches ? THEMES.DARK : THEMES.LIGHT)

    mediaQuery.addEventListener?.('change', handleChange)
    return () => mediaQuery.removeEventListener?.('change', handleChange)
  }, [])

  // Sync with document DOM
  useEffect(() => {
    applyThemeToDocument(theme, resolvedTheme)
  }, [theme, resolvedTheme])

  // Listen to storage events for cross-tab sync
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const handleStorage = (event) => {
      if (event.key === THEME_STORAGE_KEY && event.newValue && VALID_THEMES.has(event.newValue)) {
        setThemeState(event.newValue)
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const setTheme = useCallback((nextTheme) => {
    const validated = VALID_THEMES.has(nextTheme) ? nextTheme : THEMES.SYSTEM
    setThemeState(validated)
    try {
      if (typeof window !== 'undefined') {
        window.localStorage?.setItem(THEME_STORAGE_KEY, validated)
      }
    } catch {
      // Local storage may be restricted
    }
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK)
  }, [resolvedTheme, setTheme])

  const contextValue = useMemo(() => ({
    theme,
    resolvedTheme,
    isDark,
    setTheme,
    toggleTheme,
    themes: THEMES,
  }), [theme, resolvedTheme, isDark, setTheme, toggleTheme])

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  )
}
